import { useCallback, useMemo } from 'react';
import { CancelToken } from 'axios';
import useQuery, { QueryKey } from 'hooks/useQuery';
import useRecommendationLinkedRecords from 'screens/Recommendations/hooks/useRecommendationLinkedRecords';
import {
  RecommendationLinkedRecord,
  RecommendationLinkedRecordsResponse
} from 'screens/Recommendations/hooks/linkedRecords.types';
import { RecordSourceType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { LinkedRecordRow, LinkedRecordType } from '../types';

interface UseLinkedRecordsReturn {
  rows: LinkedRecordRow[];
  totalRecords: number;
  isLoading: boolean;
  hasError: boolean;
}

const RECORD_TYPES: Record<string, LinkedRecordType> = {
  WORK_ORDER: RecordSourceType.WORK_ORDER,
  APPLICATION: RecordSourceType.APPLICATION
};

/**
 * Rows whose `recordType` isn't one the drawer can open are dropped rather than
 * rendered with a blank icon and a dead View More.
 */
const toRows = (records: RecommendationLinkedRecord[]): LinkedRecordRow[] =>
  records.reduce<LinkedRecordRow[]>((rows, record) => {
    const type = record.recordType && RECORD_TYPES[record.recordType];
    return type ? [...rows, { ...record, type }] : rows;
  }, []);

/** `page`/`size` are part of the cache key, so changing page refetches. */
const useLinkedRecords = (
  recommendationId: string | null | undefined,
  page: number,
  size: number
): UseLinkedRecordsReturn => {
  const { getLinkedRecords } = useRecommendationLinkedRecords();

  const queryFn = useCallback(
    (key: QueryKey, cancelToken?: CancelToken) => {
      if (!key || typeof key !== 'string') {
        throw new Error('Recommendation ID is required');
      }
      const [id, pageValue, sizeValue] = key.split('|');
      return getLinkedRecords(
        {
          recommendationId: id,
          page: Number(pageValue),
          size: Number(sizeValue)
        },
        cancelToken
      );
    },
    [getLinkedRecords]
  );

  const queryKey = recommendationId
    ? `${recommendationId}|${page}|${size}`
    : null;

  const { data, errors, isLoading } = useQuery<
    RecommendationLinkedRecordsResponse
  >(queryKey, queryFn, {
    cacheKeyPrefix: 'recommendationLinkedRecords',
    cacheTTL: 0
  });

  const rows = useMemo(() => toRows(data?.results ?? []), [data]);

  return {
    rows,
    totalRecords: data?.totalElements ?? 0,
    isLoading,
    hasError: Boolean(errors)
  };
};

export default useLinkedRecords;
